import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { Chip, IconButton, Box } from '@mui/material';

import { DataTable } from 'src/components/common/table/DataTable';
import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import {
	IColumn,
	ITableQueryState,
} from 'src/components/common/types/tableTypes';
import { IUser, IUserTableProps } from 'src/features/user/domain/types';
import { getUsers, USER_LOADING_KEY } from 'src/features/user/store/userThunks';
import { AppDispatch } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';
import { snakeToTitleCase } from 'src/utils/formatter';

import { UserTableActions } from './UserTableActions';

export const UserTable: React.FC<IUserTableProps> = ({ refreshTable }) => {
	const dispatch = useDispatch<AppDispatch>();

	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [users, setUsers] = useState<IUser[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [clickedRow, setClickedRow] = useState<IUser>();
	const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(
		null,
	);

	const handleMenuClick = (
		event: React.MouseEvent<HTMLElement>,
		user: IUser,
	) => {
		setClickedRow(user);
		setAnchorElement(event.currentTarget);
	};

	const columns: IColumn<IUser>[] = [
		{
			key: 'name',
			label: 'Name',
			sortable: true,
			searchable: true,
			width: '17%',
			render: (user) => `${user.first_name} ${user.last_name}`,
		},
		{
			key: 'email',
			label: 'Email',
			searchable: true,
			width: '20%',
			render: (user) => (
				<Box
					sx={{
						wordBreak: 'break-word',
						whiteSpace: 'normal',
					}}
				>
					{user.email}
				</Box>
			),
		},
		{
			key: 'type',
			label: 'Type',
			filterable: true,
			menuItems: [
				{ label: 'Super Admin', value: 'super_admin' },
				{ label: 'General', value: 'general' },
			],
			render: (user) => (
				<Chip
					label={snakeToTitleCase(user.type)}
					variant='outlined'
					size='small'
				/>
			),
		},
		{
			key: 'status',
			label: 'Status',
			filterable: true,
			menuItems: [
				{ label: 'Pending', value: 'pending' },
				{ label: 'Active', value: 'active' },
				{ label: 'Deactivated', value: 'deactivated' },
			],
			multiFilter: true,
			render: (user) => (
				<Chip
					label={snakeToTitleCase(user.status)}
					variant='outlined'
					size='small'
				/>
			),
		},
		{
			key: 'created_at',
			label: 'Created At',
			sortable: true,
			width: '20%',
			render: (user) => formatTimestamp(user.created_at),
		},
		{
			key: 'actions',
			label: 'Actions',
			render: (user) => (
				<IconButton onClick={(e) => handleMenuClick(e, user)}>
					<MoreVertOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleMenuClose = () => {
		setAnchorElement(null);
		setClickedRow(undefined);
	};

	useEffect(() => {
		if (refreshTable.refreshTable) {
			setQueryState({ page: 0 });
		}
	}, [refreshTable]);

	useEffect(() => {
		const params = buildQueryParams(queryState);
		dispatch(
			getUsers({
				request: params,
				onSuccess: (response) => {
					setUsers(response.results);
					setTotalCount(response.info.count);
				},
			}),
		);
	}, [queryState]);

	return (
		<>
			<DataTable
				columns={columns}
				data={users}
				loadingKey={USER_LOADING_KEY}
				emptyMessage='No users found!'
				paginationInfo={{ totalCount: totalCount }}
				queryState={queryState}
				onQueryChange={setQueryState}
			/>

			{clickedRow && (
				<UserTableActions
					selectedRow={clickedRow}
					anchorElement={anchorElement}
					clearAnchorElement={() => setAnchorElement(null)}
					OnActionHit={(preserveState) =>
						setQueryState(
							preserveState ? { ...queryState } : { page: 0 },
						)
					}
					onClose={handleMenuClose}
				/>
			)}
		</>
	);
};
